import React, { useEffect, useMemo } from 'react';
import CalculatorWidget from '../components/CalculatorWidget';
import { EMBED_LABELS } from '../lib/embedLabels';

/**
 * Chrome-free calculator for third-party sites (dealers), embedded via iframe:
 *   /embed/calculator?lang=el&wa=306978881778&mail=info@powerlab.works&ref=carelectronics
 *
 * All parameters are validated; nothing from the URL is ever used as a link
 * target or rendered as HTML.
 *   lang  en | el | tr           (default en)
 *   wa    digits only, 8-15      (WhatsApp number of the dealer, no "+")
 *   mail  e-mail address         (where the "request quote" mail goes)
 *   ref   [a-z0-9_-]{1,32}       (dealer tag: added to mail subject, WA text, powered-by link)
 */
const clean = (q) => {
  const lang = ['en', 'el', 'tr'].includes(q.get('lang')) ? q.get('lang') : 'en';
  const waRaw = (q.get('wa') || '').replace(/[^0-9]/g, '');
  const wa = /^[0-9]{8,15}$/.test(waRaw) ? waRaw : '';
  const mailRaw = (q.get('mail') || '').trim();
  const mail = /^[^\s@<>"',;]{1,64}@[^\s@<>"',;]{1,255}\.[A-Za-z]{2,}$/.test(mailRaw) ? mailRaw : '';
  const refRaw = (q.get('ref') || '').toLowerCase();
  const ref = /^[a-z0-9_-]{1,32}$/.test(refRaw) ? refRaw : '';
  return { lang, wa, mail, ref };
};

const EmbedCalculator = () => {
  const opts = useMemo(() => clean(new URLSearchParams(window.location.search)), []);
  const L = EMBED_LABELS[opts.lang];
  const base = EMBED_LABELS.en;
  const labels = { ...base, ...L };

  useEffect(() => {
    document.documentElement.setAttribute('lang', opts.lang);
    document.title = 'Power Calculator';
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex,nofollow';
    document.head.appendChild(m);
    const prev = document.body.style.background;
    document.body.style.background = 'transparent';
    return () => { document.body.style.background = prev; if (m.parentNode) m.parentNode.removeChild(m); };
  }, [opts.lang]);

  // Optional: tell a parent page our content height (harmless if nobody listens).
  useEffect(() => {
    const send = () => {
      try {
        window.parent.postMessage({ type: 'powerlab-embed-height', height: document.documentElement.scrollHeight }, '*');
      } catch (e) { /* ignore */ }
    };
    send();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(send) : null;
    if (ro) ro.observe(document.body);
    return () => { if (ro) ro.disconnect(); };
  }, []);

  const poweredHref = `https://powerlab.works/calculator?utm_source=embed${opts.ref ? `&utm_medium=${opts.ref}` : ''}`;

  return (
    <div className="p-2 sm:p-3" data-testid="embed-calculator">
      <CalculatorWidget labels={labels} contact={{ wa: opts.wa, mail: opts.mail, ref: opts.ref, embed: true }} />
      <p className="text-center mt-3 text-xs text-gray-500">
        <a href={poweredHref} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF6600] underline-offset-2 hover:underline">
          {labels.poweredBy}
        </a>
      </p>
    </div>
  );
};

export default EmbedCalculator;
